import React, { useState } from 'react';
import Icon from '@mdi/react';
import * as mdiIcons from '@mdi/js';
import { MARKER_COLORS } from './markerUtils';

interface ThumbnailMarkerSvgProps {
  thumbnailUrl: string;
  iconName: string;
  isHighlighted?: boolean;
}

const SIZE = 36;
const HIGHLIGHTED_SIZE = 44;
const BORDER_WIDTH = 2.5;
const POINTER_HEIGHT = 7;

const getMdiIconPath = (name: string): string => {
  const iconKey = 'mdi' + name.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join('');
  return (mdiIcons as any)[iconKey] || mdiIcons.mdiNewspaper;
};

export const ThumbnailMarkerSvg: React.FC<ThumbnailMarkerSvgProps> = ({
  thumbnailUrl,
  iconName,
  isHighlighted = false,
}) => {
  const [loaded, setLoaded] = useState(false);
  const size = isHighlighted ? HIGHLIGHTED_SIZE : SIZE;
  const radius = size / 2;
  const svgWidth = size + BORDER_WIDTH * 2;
  const svgHeight = size + BORDER_WIDTH * 2 + POINTER_HEIGHT;
  const cx = svgWidth / 2;
  const cy = radius + BORDER_WIDTH;
  const iconSize = size * 0.55;

  const clipId = `thumb-clip-${size}`;

  return (
    <div style={styles.container}>
      <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
        <defs>
          <clipPath id={clipId}>
            <circle cx={cx} cy={cy} r={radius} />
          </clipPath>
        </defs>

        {/* White border circle */}
        <circle cx={cx} cy={cy} r={radius + BORDER_WIDTH} fill="white" />

        {/* White border pointer */}
        <polygon
          points={`${cx - 8},${cy + radius - 1} ${cx},${svgHeight} ${cx + 8},${cy + radius - 1}`}
          fill="white"
        />

        {/* Placeholder background */}
        {!loaded && (
          <circle cx={cx} cy={cy} r={radius} fill={MARKER_COLORS.recorded} />
        )}

        {/* Thumbnail image (hidden until loaded) */}
        <image
          href={thumbnailUrl}
          x={cx - radius}
          y={cy - radius}
          width={size}
          height={size}
          clipPath={`url(#${clipId})`}
          preserveAspectRatio="xMidYMid slice"
          opacity={loaded ? 1 : 0}
          onLoad={() => setLoaded(true)}
        />
      </svg>

      {/* Icon overlay while loading */}
      {!loaded && (
        <div style={{ ...styles.iconOverlay, top: cy - iconSize / 2, left: cx - iconSize / 2 }}>
          <Icon path={getMdiIconPath(iconName)} size={`${iconSize}px`} color="white" />
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative' as const,
  },
  iconOverlay: {
    position: 'absolute' as const,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'none' as const,
  },
};
